import { rootApi } from 'root-api-graphql'

const GetContactsSettingsQuery = `
  query GetContactsSettings {
    getContactsSettings {
      calendlyBookingEnabled
      notetakerEnabled
      gmailEnabled
      excludedDomainsAndEmails
    }
  }
`

const ProviderImportContextFields = `
      isConnected
      forms {
        id
        title
        isAlreadyConnected
      }
`

const GetProviderImportContextQuery = `
  query GetProviderImportContext($provider: FormProvider!) {
    providerImportContext(provider: $provider) {
${ProviderImportContextFields}
    }
  }
`

const GetGoogleFormsImportContextQuery = `
  query GetGoogleFormsImportContext {
    googleFormsImportContext {
${ProviderImportContextFields}
    }
  }
`

const DisconnectProviderAccountMutation = `
  mutation DisconnectProviderAccount($provider: FormProvider!, $input: ProviderAccountDeleteType!) {
    disconnectProviderAccount(provider: $provider, input: $input)
  }
`

const DisconnectGoogleFormsAccountMutation = `
  mutation DisconnectGoogleFormsAccount($input: ProviderAccountDeleteType!) {
    disconnectGoogleFormsAccount(input: $input)
  }
`

const UpdateContactsSettingsMutation = `
  mutation UpdateContactsSettings($input: ContactsSettingsInput!) {
    updateContactsSettings(input: $input) {
      calendlyBookingEnabled
      notetakerEnabled
      gmailEnabled
      excludedDomainsAndEmails
    }
  }
`

const GetEmailDigestPreferencesQuery = `
  query GetEmailDigestPreferences {
    emailDigestPreferences {
      meetingPrep {
        enabled
      }
    }
  }
`

const UpdateEmailDigestPreferencesMutation = `
  mutation UpdateEmailDigestPreferences($input: EmailDigestPreferencesInput!) {
    updateEmailDigestPreferences(input: $input) {
      meetingPrep {
        enabled
      }
    }
  }
`

const UpdateFollowUpEmailPreferencesMutation = `
  mutation UpdateFollowUpEmailPreferences($input: FollowUpEmailPreferencesInput!) {
    updateFollowUpEmailPreferences(input: $input) {
      enabled
    }
  }
`

const getBaseUrl = () => {
  const value = typeof window !== 'undefined' && window.OAP_API_GRAPHQL_BASE
  return (value && String(value)) || '/api/oap-api/graphql'
}

const api = rootApi.enhanceEndpoints({
  addTagTypes: ['ContactsSettings', 'GoogleFormsImportContext', 'ProviderImportContext', 'EmailDigestPreferences'],
}).injectEndpoints({
  endpoints: (build) => ({
    getContactsSettings: build.query({
      query: () => ({
        document: GetContactsSettingsQuery,
        meta: { baseUrl: getBaseUrl(), fetchAuthToken: true, treatGraphqlErrorsAsFailure: true },
      }),
      providesTags: ['ContactsSettings'],
    }),
    getProviderImportContext: build.query({
      query: (provider) => ({
        document: GetProviderImportContextQuery,
        variables: { provider },
        meta: { baseUrl: getBaseUrl(), fetchAuthToken: true, treatGraphqlErrorsAsFailure: true },
      }),
      providesTags: ['ProviderImportContext'],
    }),
    getGoogleFormsImportContext: build.query({
      query: () => ({
        document: GetGoogleFormsImportContextQuery,
        meta: { baseUrl: getBaseUrl(), fetchAuthToken: true, treatGraphqlErrorsAsFailure: true },
      }),
      providesTags: ['GoogleFormsImportContext'],
    }),
    disconnectProviderAccount: build.mutation({
      query: ({ provider, input }) => ({
        document: DisconnectProviderAccountMutation,
        variables: { provider, input },
        meta: { baseUrl: getBaseUrl(), fetchAuthToken: true, treatGraphqlErrorsAsFailure: true },
      }),
    }),
    disconnectGoogleFormsAccount: build.mutation({
      query: (input) => ({
        document: DisconnectGoogleFormsAccountMutation,
        variables: { input },
        meta: { baseUrl: getBaseUrl(), fetchAuthToken: true, treatGraphqlErrorsAsFailure: true },
      }),
    }),
    updateContactsSettings: build.mutation({
      query: (input) => ({
        document: UpdateContactsSettingsMutation,
        variables: { input },
        meta: { baseUrl: getBaseUrl(), fetchAuthToken: true, treatGraphqlErrorsAsFailure: true },
      }),
    }),
    getEmailDigestPreferences: build.query({
      query: () => ({
        document: GetEmailDigestPreferencesQuery,
        meta: { baseUrl: getBaseUrl(), fetchAuthToken: true, treatGraphqlErrorsAsFailure: true },
      }),
      providesTags: ['EmailDigestPreferences'],
    }),
    updateEmailDigestPreferences: build.mutation({
      query: (input) => ({
        document: UpdateEmailDigestPreferencesMutation,
        variables: { input },
        meta: { baseUrl: getBaseUrl(), fetchAuthToken: true, treatGraphqlErrorsAsFailure: true },
      }),
      invalidatesTags: ['EmailDigestPreferences'],
    }),
    updateFollowUpEmailPreferences: build.mutation({
      query: (input) => ({
        document: UpdateFollowUpEmailPreferencesMutation,
        variables: { input },
        meta: { baseUrl: getBaseUrl(), fetchAuthToken: true, treatGraphqlErrorsAsFailure: true },
      }),
    }),
  })
})

export const {
  useDisconnectGoogleFormsAccountMutation,
  useDisconnectProviderAccountMutation,
  useGetGoogleFormsImportContextQuery,
  useGetProviderImportContextQuery,
  useGetEmailDigestPreferencesQuery,
  useUpdateEmailDigestPreferencesMutation,
  useUpdateFollowUpEmailPreferencesMutation,
} = api

export const updateCachedGoogleFormsImportContext = (updater) => (
  api.util.updateQueryData('getGoogleFormsImportContext', undefined, updater)
)
export const updateCachedProviderImportContext = (provider, updater) => (
  api.util.updateQueryData('getProviderImportContext', provider, updater)
)
